import { faCamera, faClose } from "@fortawesome/free-solid-svg-icons"
import { BrowserQRCodeReader } from "@zxing/browser"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { ButtonIcon } from "../../components/Button/Button"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"

interface TimeClockQrCodeScannerPopupProps {
  onScan: (text: string) => void
  onClose: () => void
}

export const TimeClockQrCodeScannerPopup: React.FC<TimeClockQrCodeScannerPopupProps> = ({ onScan, onClose }) => {
  const { t } = useTranslation(["timeClock", "helpPopup"])

  const videoRef = useRef<HTMLVideoElement>(null)

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  useEffect(() => {
    const reader = new BrowserQRCodeReader()

    let isActive = true

    let stopScanning: (() => void) | undefined

    reader
      .decodeFromVideoDevice(undefined, videoRef.current ?? undefined, (result, _error, controls) => {
        stopScanning = controls.stop

        if (result && isActive) {
          isActive = false

          controls.stop()

          onScan(result.getText())
        }
      })
      .catch(() => {
        if (isActive) setErrorMessage(t("my.wizard.camera_error"))
      })

    return () => {
      isActive = false

      stopScanning?.()
    }
  }, [onScan, t])

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("my.wizard.scan_title")}
              icon={faCamera}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-qr-scanner"
                    title={t("helpPopup:global.timeClockQrScanner.title")}
                    description={t("helpPopup:global.timeClockQrScanner.description")}
                    bullets={t("helpPopup:global.timeClockQrScanner.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockQrCodeScannerPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="flex flex-col gap-3 p-2">
              <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                <p className="text-xs text-slate-500">{t("my.wizard.scan_description")}</p>
              </Animate>

              <div className="overflow-hidden rounded-2xl border-2 border-slate-200 bg-black">
                <video ref={videoRef} className="w-full" muted playsInline />
              </div>

              {errorMessage && (
                <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                  <p className="rounded-lg border border-red-300 bg-red-50 p-2 text-xs text-red-900">{errorMessage}</p>
                </Animate>
              )}
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
